import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_COLORS = {
  inquiry: { bg: "#fbbf24", label: "Inquiry" },        // amber
  confirmed: { bg: "#22c55e", label: "Confirmed" },    // green
  in_progress: { bg: "#E11D2A", label: "In progress" },// red
  completed: { bg: "#3b82f6", label: "Completed" },    // blue
  cancelled: { bg: "#6b7280", label: "Cancelled" },    // gray
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function toISODate(d) {
  // local YYYY-MM-DD
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminCalendar() {
  const [bookings, setBookings] = useState([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings").then((r) => {
      setBookings(r.data);
      setLoading(false);
    });
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.event_date) continue;
      const key = b.event_date.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(b);
    }
    return map;
  }, [bookings]);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const startPad = first.getDay(); // 0 = Sun
    const days = [];
    // padding before
    for (let i = 0; i < startPad; i++) {
      const d = new Date(first);
      d.setDate(d.getDate() - (startPad - i));
      days.push({ date: d, outside: true });
    }
    // this month
    for (let i = 1; i <= last.getDate(); i++) {
      days.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), i), outside: false });
    }
    // padding after (to complete grid to 42 cells)
    while (days.length % 7 !== 0 || days.length < 42) {
      const dLast = days[days.length - 1].date;
      const d = new Date(dLast);
      d.setDate(d.getDate() + 1);
      days.push({ date: d, outside: d.getMonth() !== cursor.getMonth() });
      if (days.length >= 42) break;
    }
    return days;
  }, [cursor]);

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const today = () => setCursor(new Date());

  const todayISO = toISODate(new Date());

  return (
    <div data-testid="admin-calendar" className="p-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2">Schedule</div>
          <h1 className="font-serif-display text-4xl">Calendar</h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="cal-prev"
            onClick={prevMonth}
            className="p-2 border border-white/10 rounded-sm hover:bg-white/5 transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <div
            data-testid="cal-title"
            className="min-w-[180px] text-center font-serif-display text-xl"
          >
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <button
            data-testid="cal-next"
            onClick={nextMonth}
            className="p-2 border border-white/10 rounded-sm hover:bg-white/5 transition-colors"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
          <button
            data-testid="cal-today"
            onClick={today}
            className="ml-2 px-4 py-2 text-xs uppercase tracking-widest border border-white/10 rounded-sm hover:bg-white/5 transition-colors"
          >
            Today
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 flex flex-wrap gap-4 text-xs">
        {Object.entries(STATUS_COLORS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-neutral-400">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: v.bg }}
            />
            <span>{v.label}</span>
          </div>
        ))}
      </div>

      <div className="bg-[#0a0a0c] border border-white/10 rounded-sm overflow-hidden">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-white/10">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-3 py-3 overline text-neutral-500 text-center">
              {w}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="p-6 text-neutral-500 text-sm">Loading…</div>
        ) : (
          <div className="grid grid-cols-7">
            {grid.map((cell, idx) => {
              const iso = toISODate(cell.date);
              const dayBookings = byDate[iso] || [];
              const isToday = iso === todayISO;
              return (
                <button
                  key={idx}
                  data-testid={`cal-day-${iso}`}
                  onClick={() => setSelected({ iso, bookings: dayBookings })}
                  className={`min-h-[110px] text-left p-2 border-b border-r border-white/5 transition-colors hover:bg-white/5 ${
                    cell.outside ? "bg-black/30" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${
                        cell.outside ? "text-neutral-600" : "text-neutral-300"
                      } ${isToday ? "bg-[#E11D2A] text-white rounded-full w-6 h-6 flex items-center justify-center" : ""}`}
                    >
                      {cell.date.getDate()}
                    </span>
                    {dayBookings.length > 0 && (
                      <span className="text-[10px] text-neutral-500">
                        {dayBookings.length}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 space-y-1">
                    {dayBookings.slice(0, 3).map((b) => {
                      const color = STATUS_COLORS[b.status]?.bg || "#6b7280";
                      return (
                        <div
                          key={b.id}
                          className="flex items-center gap-1.5 truncate"
                          title={`${b.client_name} · ${b.event_type} · ${b.status}`}
                        >
                          <span
                            className="inline-block w-2 h-2 rounded-full shrink-0"
                            style={{ background: color }}
                          />
                          <span className="text-[11px] text-neutral-300 truncate">
                            {b.event_time ? `${b.event_time} ` : ""}
                            {b.client_name}
                          </span>
                        </div>
                      );
                    })}
                    {dayBookings.length > 3 && (
                      <div className="text-[10px] text-neutral-500">
                        + {dayBookings.length - 3} more
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selected && selected.bookings.length > 0 && (
        <DayDrawer
          iso={selected.iso}
          bookings={selected.bookings}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function DayDrawer({ iso, bookings, onClose }) {
  return (
    <div
      data-testid="cal-day-drawer"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0a0a0c] border-l border-white/10 h-full overflow-y-auto p-8"
      >
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="overline text-neutral-500">Bookings on</div>
            <h2 className="font-serif-display text-3xl mt-2">{iso}</h2>
          </div>
          <button
            data-testid="cal-drawer-close"
            onClick={onClose}
            className="text-neutral-400 hover:text-white text-sm uppercase tracking-widest"
          >
            Close
          </button>
        </div>

        <div className="space-y-4">
          {bookings.map((b) => {
            const color = STATUS_COLORS[b.status]?.bg || "#6b7280";
            return (
              <div
                key={b.id}
                data-testid={`cal-drawer-booking-${b.id}`}
                className="border border-white/10 rounded-sm p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ background: color }}
                  />
                  <span className="overline text-neutral-400">{b.status}</span>
                  {b.event_time && (
                    <span className="ml-auto text-sm text-neutral-300">
                      {b.event_time}
                    </span>
                  )}
                </div>
                <div className="text-white font-serif-display text-xl">
                  {b.client_name}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {b.client_email} · {b.client_phone}
                </div>
                <div className="mt-3 flex justify-between text-sm">
                  <span className="text-neutral-400">{b.event_type}</span>
                  <span className="text-neutral-300">
                    ₹{(b.total_amount || 0).toLocaleString()}
                  </span>
                </div>
                {b.location && (
                  <div className="text-xs text-neutral-500 mt-1">
                    <span className="overline mr-1">Loc:</span> {b.location}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
